import { BALANCE, BUILDING_DEFS, WATER_ADJACENT_RANGE, colOf, inBounds, xOf, yOf } from '../constants';
import type { Simulation } from '../sim';
import { FLAG_PIPE, type Building } from '../types';
import { hasFlag } from '../world/terrain';
import { pushLedger } from './economy';
import { updateFunctional } from './power';

function footprintCenter(building: Building): { cx: number; cy: number } {
  const def = BUILDING_DEFS[building.type];
  return { cx: building.x + def.w / 2, cy: building.y + def.h / 2 };
}

function nearAnyTank(building: Building, tanks: Building[]): boolean {
  const { cx, cy } = footprintCenter(building);
  for (const tank of tanks) {
    const t = footprintCenter(tank);
    if (Math.max(Math.abs(cx - t.cx), Math.abs(cy - t.cy)) <= WATER_ADJACENT_RANGE) return true;
  }
  return false;
}

/** A building taps the water network via any connected pipe under its
 * footprint or on the surrounding ring — lines run beneath buildings. */
function touchesConnectedPipe(building: Building, connected: Set<number>): boolean {
  const def = BUILDING_DEFS[building.type];
  for (let dy = -1; dy <= def.h; dy += 1) {
    for (let dx = -1; dx <= def.w; dx += 1) {
      const x = building.x + dx;
      const y = building.y + dy;
      if (inBounds(x, y) && connected.has(colOf(x, y))) return true;
    }
  }
  return false;
}

/** BFS the pipe grid from tanks; buildings connect via adjacency or proximity. */
export function rebuildWaterConnectivity(sim: Simulation): void {
  const state = sim.state;
  const terrain = state.terrain;
  const tanks = state.buildings.filter((building) => building.type === 'waterTank');

  state.water.tankCapacity = tanks.length * (BUILDING_DEFS.waterTank.waterCapacity ?? 500);
  state.water.tankLevelUnits = Math.min(state.water.tankLevelUnits, state.water.tankCapacity);

  // Seed the BFS from pipes under or around each tank (footprint included).
  const connected = new Set<number>();
  const queue: number[] = [];
  for (const tank of tanks) {
    const def = BUILDING_DEFS[tank.type];
    for (let dy = -1; dy <= def.h; dy += 1) {
      for (let dx = -1; dx <= def.w; dx += 1) {
        const x = tank.x + dx;
        const y = tank.y + dy;
        if (!inBounds(x, y)) continue;
        const col = colOf(x, y);
        if (hasFlag(terrain, col, FLAG_PIPE) && !connected.has(col)) {
          connected.add(col);
          queue.push(col);
        }
      }
    }
  }
  while (queue.length > 0) {
    const col = queue.pop()!;
    const x = xOf(col);
    const y = yOf(col);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      if (!inBounds(x + dx, y + dy)) continue;
      const next = colOf(x + dx, y + dy);
      if (hasFlag(terrain, next, FLAG_PIPE) && !connected.has(next)) {
        connected.add(next);
        queue.push(next);
      }
    }
  }
  sim.connectedPipes = connected;

  for (const building of state.buildings) {
    const def = BUILDING_DEFS[building.type];
    if (def.waterPerDay === undefined && building.type !== 'well') {
      building.watered = true;
      continue;
    }
    building.watered = tanks.length > 0 && (nearAnyTank(building, tanks) || touchesConnectedPipe(building, connected));
  }
  updateFunctional(state);
}

/** Daily: refill arrival, well yield, consumption, auto-refill ordering. */
export function stepWaterDaily(sim: Simulation): void {
  const state = sim.state;
  const water = state.water;
  const day = sim.day;

  if (water.refillArrivesOnDay !== null && day >= water.refillArrivesOnDay) {
    water.tankLevelUnits = Math.min(water.tankCapacity, water.tankLevelUnits + BALANCE.waterRefillUnits);
    water.refillArrivesOnDay = null;
    sim.pushEvent({ kind: 'toast', message: 'Water truck arrived.' });
  }

  for (const building of state.buildings) {
    if (building.type !== 'well') continue;
    const def = BUILDING_DEFS.well;
    if (building.watered && building.powered && building.powerEff >= 0.5) {
      const yieldUnits = Math.floor((def.wellYieldPerDay ?? 0) * building.powerEff);
      water.tankLevelUnits = Math.min(water.tankCapacity, water.tankLevelUnits + yieldUnits);
    }
  }

  let demand = state.peeps.length * BALANCE.waterUnitsPerWorkerPerDay;
  for (const building of state.buildings) {
    const def = BUILDING_DEFS[building.type];
    if (def.waterPerDay !== undefined && building.functional) demand += def.waterPerDay;
  }
  // Partial supply degrades morale proportionally instead of falling off a
  // cliff the day the tank runs dry.
  const consumed = Math.min(demand, water.tankLevelUnits);
  water.supplyRatio = demand > 0 ? consumed / demand : 1;
  water.tankLevelUnits = Math.max(0, water.tankLevelUnits - demand);

  if (
    water.autoRefill &&
    water.refillArrivesOnDay === null &&
    water.tankLevelUnits < water.tankCapacity * BALANCE.waterRefillThreshold
  ) {
    orderWaterRefill(sim);
  }
}

export function orderWaterRefill(sim: Simulation): boolean {
  const state = sim.state;
  if (state.water.refillArrivesOnDay !== null || state.meta.gameOver) return false;
  state.economy.cashCents -= BALANCE.waterRefillCents;
  state.water.refillArrivesOnDay = sim.day + 1;
  pushLedger(state, sim.day, 'water', -BALANCE.waterRefillCents, 'Water delivery ordered');
  return true;
}
